/**
 * <variant-selects> — PDP text variant picker.
 */

import { isLowStock } from '@theme/low-stock';
import { formatMoney } from '@theme/money';

class VariantSelects extends HTMLElement {
  #variants = [];
  #isComingSoon = false;
  #resizeObserver = null;

  connectedCallback() {
    const jsonEl = this.querySelector('[data-ref="variants-json"]');
    const inventoryEl = this.querySelector('[data-ref="variants-inventory-json"]');
    const inventoryById = JSON.parse(inventoryEl?.textContent || '{}');

    this.#variants = JSON.parse(jsonEl?.textContent || '[]').map((variant) => ({
      ...variant,
      ...(inventoryById[String(variant.id)] ?? {}),
    }));
    this.#isComingSoon = this.dataset.comingSoon === 'true';

    this.addEventListener('change', this.#onChange);
    this.#resizeObserver = new ResizeObserver(() => {
      this.#updateSizeGuideLayout();
    });

    this.querySelectorAll('[data-ref="variant-track"]').forEach((track) => {
      this.#resizeObserver.observe(track);
    });

    this.#resolveDefaultSelections();
    this.#selectFirstVariantIfAllSoldOut();
    this.#updateOptionStates();
    this.#updateVariant();
    requestAnimationFrame(() => {
      this.#updateSizeGuideLayout();
    });
  }

  #isGiftCard() {
    return this.dataset.giftCard === 'true';
  }

  #formatPrice(cents) {
    return formatMoney(cents);
  }

  #syncGiftCardPrices() {
    if (!this.#isGiftCard()) return;

    const root = this.closest('product-component') ?? document;
    const priceEl = root.querySelector('.pdp-header__price-amount');
    const variant = this.#findVariant() ?? this.#variants.find((entry) => entry.available);

    if (variant && priceEl) {
      const formatted = this.#formatPrice(variant.price);

      if (formatted) {
        priceEl.textContent = formatted;
      }

      priceEl.dataset.price = String(variant.price);
    }

    this.querySelectorAll('[data-gift-card-denominations] .pdp-variant-option__input[data-variant-id]').forEach(
      (input) => {
        const entry = this.#variants.find((variant) => variant.id === Number(input.dataset.variantId));
        const label = this.querySelector(`label[for="${CSS.escape(input.id)}"]`);

        if (!label || !entry?.available) return;

        const formatted = this.#formatPrice(entry.price);

        if (formatted) {
          label.textContent = formatted;
        }
      },
    );
  }

  #syncGiftCardPricesWhenReady() {
    if (!this.#isGiftCard()) return;
    this.#syncGiftCardPrices();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  #onChange = (event) => {
    if (!event.target.matches('.pdp-variant-option__input')) return;
    this.#updateOptionStates();
    this.#updateSizeGuideLayout();
    this.#updateVariant();
  };

  #getSelectedOptions() {
    /** @type {Record<string, string | null>} */
    const selected = {};

    this.querySelectorAll('.pdp-variant-option').forEach((fieldset) => {
      const position = fieldset.dataset.optionPosition;
      const checked = fieldset.querySelector('.pdp-variant-option__input:checked');
      selected[position] = checked?.value ?? null;
    });

    return selected;
  }

  #findVariant() {
    if (this.#isGiftCard()) {
      const checked = this.querySelector('[data-gift-card-denominations] .pdp-variant-option__input:checked');
      const variantId = Number(checked?.dataset.variantId);
      if (!variantId) return null;

      return this.#variants.find((variant) => variant.id === variantId) ?? null;
    }

    const selected = this.#getSelectedOptions();
    const optionGroups = this.querySelectorAll('.pdp-variant-option');

    for (const fieldset of optionGroups) {
      const position = fieldset.dataset.optionPosition;
      if (!selected[position]) return null;
    }

    return (
      this.#variants.find((variant) =>
        variant.options.every((optionValue, index) => {
          const position = String(index + 1);
          return optionValue === selected[position];
        }),
      ) ?? null
    );
  }

  #isValueAvailable(optionIndex, value, selected) {
    if (this.#isGiftCard()) {
      const variantId = Number(value);
      const variant = this.#variants.find((entry) => entry.id === variantId);
      return Boolean(variant?.available);
    }

    return this.#variants.some((variant) => {
      if (!variant.available) return false;
      if (variant.options[optionIndex] !== value) return false;

      return variant.options.every((optionValue, index) => {
        if (index === optionIndex) return true;
        const position = String(index + 1);
        const selectedValue = selected[position];
        return !selectedValue || optionValue === selectedValue;
      });
    });
  }

  #selectFirstVariantIfAllSoldOut() {
    if (this.#findVariant()) return;
    if (!this.#variants.length) return;
    if (!this.#variants.every((variant) => !variant.available)) return;

    if (this.#isGiftCard()) {
      const firstAvailable = this.#variants.find((variant) => variant.available);
      const input = firstAvailable
        ? this.querySelector(`[data-variant-id="${firstAvailable.id}"]`)
        : null;

      if (input) input.checked = true;
      return;
    }

    const firstVariant = this.#variants[0];

    firstVariant.options.forEach((optionValue, index) => {
      const position = String(index + 1);
      const fieldset = this.querySelector(`.pdp-variant-option[data-option-position="${position}"]`);
      const input = fieldset?.querySelector(
        `.pdp-variant-option__input[value="${CSS.escape(optionValue)}"]`,
      );

      if (input) {
        input.checked = true;
      }
    });
  }

  #resolveDefaultSelections() {
    const selected = this.#getSelectedOptions();

    this.querySelectorAll('.pdp-variant-option').forEach((fieldset) => {
      const inputs = [...fieldset.querySelectorAll('.pdp-variant-option__input')];

      // "Preselect: none" leaves MULTI-value options unselected so the shopper must choose.
      // A single-value option (e.g. colour on a connected-colours product) has nothing to
      // choose — skipping it leaves its fieldset permanently unchecked, so #findVariant never
      // resolves and the buy button stays stuck on "choose size". Auto-select those regardless.
      if (fieldset.dataset.defaultMode === 'none' && inputs.length > 1) return;

      const position = Number(fieldset.dataset.optionPosition);
      const optionIndex = position - 1;
      const checked = fieldset.querySelector('.pdp-variant-option__input:checked');

      if (checked && this.#isValueAvailable(optionIndex, checked.value, selected)) return;

      const checkedIndex = checked ? inputs.indexOf(checked) : -1;
      const nextInput =
        inputs.slice(checkedIndex + 1).find((input) =>
          this.#isValueAvailable(optionIndex, input.value, selected),
        ) ??
        inputs.find((input) => this.#isValueAvailable(optionIndex, input.value, selected));

      if (nextInput) {
        nextInput.checked = true;
        selected[String(position)] = nextInput.value;
      } else if (checked) {
        checked.checked = false;
        selected[String(position)] = null;
      }
    });
  }

  #updateOptionStates() {
    const selected = this.#getSelectedOptions();

    this.querySelectorAll('.pdp-variant-option').forEach((fieldset) => {
      const position = Number(fieldset.dataset.optionPosition);
      const optionIndex = position - 1;

      fieldset.querySelectorAll('.pdp-variant-option__input').forEach((input) => {
        const label = fieldset.querySelector(`label[for="${input.id}"]`);
        if (!label) return;

        const isChecked = input.checked;
        const isAvailable = this.#isValueAvailable(optionIndex, input.value, selected);

        let state = 'default';

        if (!isAvailable) {
          state = this.#isComingSoon ? 'coming-soon' : 'sold-out';
        }

        if (isChecked) {
          if (state === 'sold-out') state = 'sold-out-selected';
          else if (state === 'coming-soon') state = 'coming-soon-selected';
          else state = 'selected';
        }

        label.className = `pdp-variant-option__label u-p3 pdp-variant-option__label--${state}`;
      });
    });
  }

  #updateSizeGuideLayout() {
    this.querySelectorAll('.pdp-variant-option').forEach((fieldset) => {
      const track = fieldset.querySelector('[data-ref="variant-track"]');
      const guideEl = fieldset.querySelector('[data-ref="size-guide"]');
      track?.setAttribute('data-size-guide-layout-ready', '');

      if (!guideEl?.classList.contains('pdp-variant-option__size-guide--visible')) {
        guideEl?.classList.remove('pdp-variant-option__size-guide--end');
        guideEl?.classList.remove('pdp-variant-option__size-guide--gap');
        return;
      }

      const items = [...fieldset.querySelectorAll('.pdp-variant-option__item')];
      if (!items.length) return;

      const firstItemRect = items[0].getBoundingClientRect();
      const allItemsOnOneRow = items.every(
        (item) => Math.abs(item.getBoundingClientRect().top - firstItemRect.top) < 1,
      );
      // The guide button is vertically centered in the track, so its top sits a couple
      // px above the labels even on the same visual row; test by vertical overlap, not
      // exact top (a strict `< 1` check missed the ~2px offset and never aligned it).
      const guideRect = guideEl.getBoundingClientRect();
      const guideInlineWithAnyItem = items.some((item) => {
        const rect = item.getBoundingClientRect();
        return guideRect.bottom > rect.top && guideRect.top < rect.bottom;
      });

      // Flush-right (margin-left:auto) ONLY when the guide + every value share one row
      // (single-row products). Figma spec 12743-53461.
      const flushRight = allItemsOnOneRow && guideInlineWithAnyItem;
      // When values wrap and the guide flows after them on a shared last row (CASE 2),
      // it needs the wider 32px inner-margin from the preceding value (vs the 24px used
      // between values); when it wraps to its own line (CASE 1) it stays flush-left.
      // Figma node 12959-142581 (desktop-inner-margin 32px).
      const inlineWrappedGap = guideInlineWithAnyItem && !allItemsOnOneRow;

      guideEl.classList.toggle('pdp-variant-option__size-guide--end', flushRight);
      guideEl.classList.toggle('pdp-variant-option__size-guide--gap', inlineWrappedGap);
    });
  }

  #updateVariant() {
    const variant = this.#findVariant();
    const root = this.closest('product-component') ?? document;
    const priceEl = root.querySelector('.pdp-header__price-amount');
    const compareEl = root.querySelector('.pdp-header__price-compare');

    if (this.#isGiftCard()) {
      this.#syncGiftCardPricesWhenReady();
    } else if (variant && priceEl) {
      priceEl.textContent = formatMoney(variant.price);
      priceEl.dataset.price = String(variant.price);

      if (compareEl) {
        if (variant.compare_at_price > variant.price) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
    }

    this.#updateSoldOutLabel(variant);

    this.#updateLowStockLabel(variant);

    if (variant?.id) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url.toString());
    }

    this.dispatchEvent(
      new CustomEvent('variant:changed', {
        bubbles: true,
        detail: {
          variant,
          new_image: variant?.featured_media?.id ?? variant?.featured_image?.id ?? null,
        },
      }),
    );
  }

  #updateLowStockLabel(variant) {
    const lowStockEl = this.querySelector('[data-ref="low-stock-label"]');
    if (!lowStockEl) return;

    lowStockEl.hidden = !isLowStock(variant);
  }

  #updateSoldOutLabel(variant) {
    const root = this.closest('product-component') ?? document;
    const soldOutEl = root.querySelector('[data-ref="sold-out-label"]');
    if (!soldOutEl) return;

    // A product in the Coming soon collection keeps that label whether or not the selected variant is
    // in stock, so it must be checked before the availability early-return.
    if (soldOutEl.dataset.isComingSoon === 'true') {
      soldOutEl.textContent = soldOutEl.dataset.labelComingSoon;
      soldOutEl.hidden = false;
      return;
    }

    const unavailable = Boolean(variant?.id && !variant.available);
    if (!unavailable) {
      soldOutEl.hidden = true;
      return;
    }

    soldOutEl.textContent = soldOutEl.dataset.labelSoldOut;
    soldOutEl.hidden = false;
  }
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects);
}
